/**
 * Cargos canónicos del onboarding.
 *
 * La lista refleja los roles más comunes en una alcaldía colombiana que
 * usaría Kaptar:
 *
 * - Alcalde(sa) — autoridad máxima.
 * - Secretario(a) de Planeación — usuario primario del Radar y formulación.
 * - Secretario(a) de Hacienda — necesario para CDP y giros.
 * - Jefe(a) de Oficina de Proyectos — responsable de radicación PIIP.
 * - Contratista / Asesor — formulador externo contratado por la alcaldía.
 * - Otro — fallback con free-text para casos no anticipados.
 *
 * Sprint 9: el backend mantiene `position` como TEXT libre; el frontend
 * normaliza a estos labels canónicos para que analytics agrupe limpio
 * (ej. "X% de los onboardings son Secretario de Planeación").
 *
 * **Convención**: si el cargo es uno de los 5 canónicos, el frontend
 * envía exactamente el `value`. Si es "Otro", el frontend envía el texto
 * que escriba el usuario. El backend no valida el set — esto es
 * disciplina UI.
 */

export interface PositionOption {
  value: string;
  label: string;
}

/** Sentinel que indica "el usuario eligió Otro y quiere escribir libre". */
export const POSITION_OTHER_SENTINEL = "__otro__";

export const POSITION_OPTIONS: PositionOption[] = [
  { value: "Alcalde(sa)", label: "Alcalde(sa)" },
  { value: "Secretario(a) de Planeación", label: "Secretario(a) de Planeación" },
  { value: "Secretario(a) de Hacienda", label: "Secretario(a) de Hacienda" },
  {
    value: "Jefe(a) de Oficina de Proyectos",
    label: "Jefe(a) de Oficina de Proyectos",
  },
  { value: "Contratista / Asesor", label: "Contratista / Asesor" },
  { value: POSITION_OTHER_SENTINEL, label: "Otro" },
];

const CANONICAL_VALUES = new Set(
  POSITION_OPTIONS.filter((o) => o.value !== POSITION_OTHER_SENTINEL).map((o) => o.value),
);

export interface ResolvedInitialPosition {
  /** Valor del select. Vacío si no hay initial. */
  selectValue: string;
  /** Texto del free-text input. Solo poblado si selectValue === sentinel. */
  otherText: string;
}

/**
 * Resuelve el estado inicial del select de cargo cuando el usuario
 * re-entra al wizard.
 *
 * - Si initialPosition matchea una opción canónica, el select arranca
 *   con esa opción y `otherText` queda vacío.
 * - Si initialPosition es un string no canónico (cargo personalizado
 *   guardado en una sesión anterior), el select arranca en "Otro" y
 *   `otherText` precarga el valor — así el dato del usuario no se pierde.
 * - Si initialPosition está vacío, ambos arrancan vacíos.
 */
export function resolveInitialPosition(initialPosition: string): ResolvedInitialPosition {
  const trimmed = initialPosition.trim();
  if (trimmed === "") return { selectValue: "", otherText: "" };
  if (CANONICAL_VALUES.has(trimmed)) {
    return { selectValue: trimmed, otherText: "" };
  }
  return { selectValue: POSITION_OTHER_SENTINEL, otherText: trimmed };
}

/**
 * Computa el valor a enviar al backend dado el estado actual del select
 * y el free-text. El backend espera string libre — esta función
 * resuelve qué string es el correcto.
 */
export function resolvePositionForSubmit(selectValue: string, otherText: string): string {
  if (selectValue === POSITION_OTHER_SENTINEL) return otherText.trim();
  return selectValue;
}
